/**
 * A part of a page that is planned and not built (D19 phase 21). The references show tabs and cards we have no data
 * for yet; the owner's rule (2026-09-28) is that such a place keeps its spot and says what it will hold and when —
 * never invented figures. The page-level version is PlannedCapability; this is the same promise inside a tab or card.
 */
import type { ReactNode } from 'react';
import { Construction } from 'lucide-react';
import { cx } from './primitives';

const SHOW_PLANNED = false;

export function PlannedPanel({ title, step, items, builtSoFar, className }: {
  /** What this place is, e.g. "Invoices". */
  title: string;
  /** The step that builds it, e.g. "v6 S5 — billing and collections". */
  step: string;
  /** What it will hold. */
  items?: readonly string[];
  /** What already exists that a person can use meanwhile, with where to find it. */
  builtSoFar?: ReactNode;
  className?: string;
}) {
  // v7 (CORE-1, rule 4; Part D §5): a place with no data yet is not shown on a staff screen. What is planned is listed on
  // Admin › Roadmap instead. The props stay so callers need not change; the card renders nothing.
  if (!SHOW_PLANNED) return null;
  return (
    <div role="note" data-planned="true" className={cx('rounded-[var(--radius-control)] border border-dashed border-line-strong bg-surface-sunken/60 px-4 py-3', className)}>
      <p className="flex items-center gap-2 text-body font-medium text-ink">
        <Construction size={15} className="shrink-0 text-admin" aria-hidden />
        {title}
        <span className="rounded-[var(--radius-badge)] border border-line px-1.5 text-[11px] uppercase tracking-wide text-ink-faint">Planned</span>
      </p>
      <p className="mt-1 text-small text-ink-muted">Not built yet: arrives with <span className="font-medium text-ink">{step}</span>. Nothing here is data.</p>
      {items && items.length > 0 ? (
        <ul className="mt-2 list-disc space-y-0.5 pl-5 text-small text-ink-muted">{items.map((i) => <li key={i}>{i}</li>)}</ul>
      ) : null}
      {builtSoFar ? <p className="mt-2 text-small text-ink-muted"><span className="font-medium text-ink">Meanwhile: </span>{builtSoFar}</p> : null}
    </div>
  );
}
